import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

// Third-party
import { useAtom, useAtomValue } from 'jotai';
import { KeyedMutator } from 'swr';

// Services
import { SocialInboxService } from '@/components/pages/conversations/SocialInbox/services/SocialInboxService';

// Hooks
import { useDMConversations } from '@/utils/hooks/swr/conversations/useDMConversations';
import { useGroupConversation } from '@/utils/hooks/swr/group/useGroupConversations';
import useComments from '@/utils/hooks/swr/useComments';
import useMentions from '@/utils/hooks/swr/useMentions';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Types
import type { SocialInboxMessage } from '@/components/pages/conversations/SocialInbox/types';

import {
    MANAGE_COMMENTS_TRACKER,
    MANAGE_DM_TRACKER,
    MANAGE_IMPORTANT_TRACKER,
    MANAGE_MENTIONS_TRACKER,
    MANAGE_RESOLVED_TRACKER
} from '@/lib/routes/privateRoutes';
// Lib/Constants
import { SOCIAL_INBOX_META } from '@/components/pages/conversations/SocialInbox/const';

// Atoms
import {
    messageFilterSecondaryAtom,
    openedMessageAtom
} from '@/components/pages/conversations/SocialInbox/atoms';

const sortMessages = (messages: SocialInboxMessage[]) => {
    return messages.sort((a, b) => {
        const dateKey = 'sent_at' in a ? 'sent_at' : 'created';
        return new Date(b[dateKey]).getTime() - new Date(a[dateKey]).getTime();
    });
};

const resolveParams = (path: string, fetchingFromGroup: boolean) => {
    if ([MANAGE_IMPORTANT_TRACKER, MANAGE_RESOLVED_TRACKER].includes(path)) {
        const socialInboxType = path.replace('/conversations/', '');
        return fetchingFromGroup
            ? { [`is_${SOCIAL_INBOX_META[socialInboxType].status}`]: true }
            : { status: SOCIAL_INBOX_META[socialInboxType].status };
    }
    return null;
};

type DataSource = {
    messages: SocialInboxMessage[];
    isLoading: boolean;
    isLoadingMore: boolean;
    isLoadMore: boolean;
    loadMore: () => void;
    mutate: KeyedMutator<any>;
    triggerFetchJob?: () => Promise<void>;
};

const useCommentsDataSource = (
    activeSocialProfileId: number | null,
    shouldFetch: boolean,
    path: string
): DataSource => {
    const prevPathRef = useRef(path);
    const resolvedParams = resolveParams(path, false);

    const {
        dataComments: socialProfileComments,
        isLoadingInitialData: isLoading,
        isLoadingMore,
        isLoadMore,
        loadMoreComments: loadMore,
        mutateComments: mutate,
        setRequestParams
    } = useComments({
        activeSPID: activeSocialProfileId,
        shouldFetch,
        params: resolvedParams,
        options: {
            revalidateOnFocus: shouldFetch,
            revalidateOnReconnect: shouldFetch,
            dedupingInterval: 2000
        }
    } as any);

    // Only update params when path changes, not on every render
    useEffect(() => {
        if (prevPathRef.current !== path) {
            prevPathRef.current = path;
            setRequestParams(resolvedParams);
        }
    }, [path, setRequestParams, resolvedParams]);

    return {
        messages: socialProfileComments?.flatMap((page) => page.data) ?? [],
        isLoading,
        isLoadingMore,
        isLoadMore,
        loadMore,
        mutate
    };
};

const useMentionsDataSource = (
    activeSocialProfileId: number | null,
    shouldFetch: boolean
): DataSource => {
    const {
        isLoadingInitialData: isLoading,
        isLoadingMore,
        isMoreToBeLoaded: isLoadMore,
        loadMore,
        mentions,
        mutate
    } = useMentions({
        activeSPID: activeSocialProfileId,
        limit: 10,
        shouldFetch,
        options: {
            revalidateOnFocus: shouldFetch,
            revalidateOnReconnect: shouldFetch
        }
    });

    const messages = mentions;

    return {
        messages,
        isLoading,
        isLoadingMore,
        isLoadMore,
        loadMore,
        mutate
    };
};

const useGroupDataSource = (shouldFetch: boolean, path: string): DataSource => {
    const {
        data: messages,
        isLoading,
        isLoadingMore,
        isLoadMore,
        loadMore,
        mutate
    } = useGroupConversation({
        shouldFetch,
        params: resolveParams(path, true),
        options: {
            revalidateOnFocus: shouldFetch,
            revalidateOnReconnect: shouldFetch
        }
    });

    return {
        messages: messages ?? [],
        isLoading,
        isLoadingMore,
        isLoadMore,
        loadMore,
        mutate
    };
};

const useDirectMessagesDataSource = (
    activeSocialProfileId: number | null,
    shouldFetch: boolean
): DataSource => {
    const { data, isLoading, mutate, isLoadingMore, isLoadMore, loadMore, triggerFetchJob } =
        useDMConversations({
            activeSPID: activeSocialProfileId,
            shouldFetch,
            options: {
                revalidateOnFocus: false,
                revalidateOnReconnect: shouldFetch
            }
        });

    return {
        messages: data?.flatMap((page) => page.data) ?? [],
        isLoading,
        isLoadingMore,
        isLoadMore,
        loadMore,
        mutate,
        triggerFetchJob
    };
};
/**
 * @description Hook to fetch comments, mentions and group conversations from a social profile or a group
 *
 */
export const useSocialInboxDataSource = () => {
    const { activeGroup } = useActiveGroup();
    const { activeSocialProfile } = useActiveSocialProfile();
    const [openedMessage, setOpenedMessage] = useAtom(openedMessageAtom);
    const filterState = useAtomValue(messageFilterSecondaryAtom);
    const path = usePathname();

    const isGroupConversation = !activeSocialProfile && activeGroup !== null;
    const isMentions = path === MANAGE_MENTIONS_TRACKER;
    const isConversation =
        !isGroupConversation &&
        [MANAGE_COMMENTS_TRACKER, MANAGE_IMPORTANT_TRACKER, MANAGE_RESOLVED_TRACKER].includes(path);
    const isDirectMessage = path === MANAGE_DM_TRACKER;

    // Initialize all data sources
    const commentsSource = useCommentsDataSource(activeSocialProfile?.id, isConversation, path);
    const mentionsSource = useMentionsDataSource(activeSocialProfile?.id, isMentions);
    const groupSource = useGroupDataSource(isGroupConversation, path);
    const directMessagesSource = useDirectMessagesDataSource(
        activeSocialProfile?.id,
        isDirectMessage
    );

    const activeSource = (() => {
        if (isGroupConversation) return groupSource;
        if (isMentions) return mentionsSource;
        if (isDirectMessage) return directMessagesSource;
        return commentsSource;
    })();

    const messages = sortMessages(activeSource.messages);

    useEffect(() => {
        if (openedMessage) {
            const updatedMessage = messages.find((message) => message.id === openedMessage.id);
            if (updatedMessage) {
                setOpenedMessage(updatedMessage);
            }
        }
    }, [messages, openedMessage, setOpenedMessage]);

    const handleMutateMessages = async (
        ...args: Parameters<KeyedMutator<SocialInboxMessage[]>>
    ) => {
        // If no data and options are provided, use SWR's standard revalidation
        if (!args[0] && args[1]) {
            return activeSource.mutate(undefined, args[1]);
        }

        // If no data, just refresh the data source
        if (!args[0]) {
            return activeSource.mutate();
        }

        const resolvedData = await Promise.resolve(args[0]);

        if (typeof resolvedData === 'function') {
            const mutatorFn = resolvedData as (
                currentData: SocialInboxMessage[]
            ) => SocialInboxMessage[];
            const updatedMessages = mutatorFn(messages);
            const dataKey = isGroupConversation ? 'comments' : 'data';

            return activeSource.mutate(
                (prev) =>
                    SocialInboxService.updateMessagesInDataSource(prev, updatedMessages, dataKey),
                args[1]
            );
        } else {
            // Direct data update
            const updatedMessages = resolvedData;
            const dataKey = isGroupConversation ? 'comments' : 'data';

            return activeSource.mutate(
                (prev) =>
                    SocialInboxService.updateMessagesInDataSource(prev, updatedMessages, dataKey),
                args[1]
            );
        }
    };

    const filteredMessages =
        filterState.filter === 'all'
            ? messages
            : messages.filter(
                  (message) =>
                      ('is_read' in message && !message.is_read) ||
                      ('seen_at' in message && !message.seen_at)
              );

    /**
     * Function to manually refresh the current data source
     * Used by the refresh button in the UI
     */
    const refreshMessages = async () => activeSource.mutate(undefined, { revalidate: true });

    return {
        filteredMessages,
        triggerFetchJob: activeSource?.triggerFetchJob,
        isLoading: activeSource.isLoading,
        isLoadingMore: activeSource.isLoadingMore,
        isLoadMore: activeSource.isLoadMore,
        loadMore: activeSource.loadMore,
        messages,
        mutateMessages: handleMutateMessages,
        refreshMessages
    };
};
